import { ChangeEvent, useMemo } from "react";
import { Box, Button, Checkbox, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { AdminLeadListResDto } from "service/api/estabrookSchemas";
import { generateRoute } from "ui";
import { RenderGenericRowFunction } from "@/shared/components/table/GenericTable";
import InteractableCell from "@/shared/components/table/InteractableCell";
import { AdRoutes } from "@/constants/ad-routes";
import useDeleteLead from "./useDeleteLead";
import { FilterState } from "./useLeadsFilters";

// Ellipsize within the cell. Paired with `customCSS: { maxWidth: 0 }` on the
// cell so the column width comes from the header `size`/`minWidth` (the body
// TableCell otherwise has no width), making truncation responsive rather than
// a fixed px cap.
const ellipsisSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

// Forces the cell to take its width from the header instead of its content.
const truncateCellCSS = { maxWidth: 0 };

const useLeadsTableRow = (
  filters: FilterState,
  handleSelection: (evt: ChangeEvent<HTMLInputElement>, leadId: string) => void,
) => {
  const navigate = useNavigate();
  const { confirmDeleteLead } = useDeleteLead();

  const onEditLead = (leadId: string) => {
    navigate(generateRoute(AdRoutes.leadDetail, { leadId }));
  };

  const onConvertToCustomer = (lead: AdminLeadListResDto) => {
    navigate(generateRoute(AdRoutes.leadConvert, { leadId: lead.id }));
  };

  return useMemo<Record<string, RenderGenericRowFunction<AdminLeadListResDto>>>(
    () => ({
      checkbox: ({ headerValue, data: { id } }) => ({
        label: (
          <Checkbox
            checked={filters.selectedLeadIds.includes(id)}
            onChange={(evt) => handleSelection(evt, id)}
          />
        ),
        value: headerValue,
      }),
      lead: ({ headerValue, data: { fullName, email } }) => ({
        label: (
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={fullName}>
              <Typography sx={ellipsisSx}>{fullName}</Typography>
            </Tooltip>
            <Tooltip title={email}>
              <Typography
                variant="caption"
                sx={{ ...ellipsisSx, fontSize: "12px", color: "#C1C7CE" }}
              >
                {email}
              </Typography>
            </Tooltip>
          </Box>
        ),
        value: headerValue,
        customCSS: truncateCellCSS,
      }),
      address: ({ headerValue, data: { address } }) => ({
        label: (
          <Tooltip title={address}>
            <Typography sx={ellipsisSx}>{address}</Typography>
          </Tooltip>
        ),
        value: headerValue,
        customCSS: truncateCellCSS,
      }),
      phoneNumber: ({ headerValue, data: { phoneNumber } }) => ({
        label: <Typography noWrap>{phoneNumber}</Typography>,
        value: headerValue,
      }),
      createdAt: ({ headerValue, data: { createdAt } }) => ({
        label: (
          <Typography noWrap>
            {createdAt
              ? DateTime.fromISO(createdAt).toFormat("LLL d, yyyy")
              : "-"}
          </Typography>
        ),
        value: headerValue,
      }),
      state: ({ headerValue, data }) => ({
        label: (
          <Button
            variant="outlined"
            size="small"
            sx={{ whiteSpace: "nowrap" }}
            onClick={() => onConvertToCustomer(data)}
          >
            Convert to Customer
          </Button>
        ),
        value: headerValue,
      }),
      actions: ({ headerValue, data }) => ({
        label: (
          <InteractableCell
            label={""}
            onEdit={() => onEditLead(data.id)}
            onDelete={() => confirmDeleteLead(data.id, data.fullName)}
          />
        ),
        value: headerValue,
      }),
    }),
    // Re-render cells when the selection changes (checkbox state).
    [filters.selectedLeadIds],
  );
};

export default useLeadsTableRow;
